import React from 'react';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { H2, P } from '@osmosis/tailwind-ui/components/Text';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-m';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { faAngleDown } from '@fortawesome/pro-solid-svg-icons/faAngleDown';
import type { LibraryTopicLevel } from '../../types/library';
import { text5, textBlue } from '@osmosis/tailwind-ui/styles';
import { useContentSize } from '@osmosis/nextjs-components/context';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';

type LibraryTopicLevelProps = {
  topic: LibraryTopicLevel;
  isTopicsOpen: boolean;
  toggleCollapseAllTopics: () => void;
};

/**
 * Library topic level.
 */
const LibraryTopicLevel = (props: LibraryTopicLevelProps) => {
  const { title, hash } = props.topic;
  const { isTopicsOpen, toggleCollapseAllTopics } = props;
  const { formatMessage } = useIntl();
  const { getClassValue } = useContentSize();

  const collapseTextResponsiveClasses = getClassValue({
    defaultClasses: 'hidden lg:inline',
    breakpoints: {
      lg: 'inline',
      base: 'hidden',
    },
  });

  const topicLevelTitleResponsiveClasses = getClassValue({
    defaultClasses: 'sm:text-2xl',
    breakpoints: {
      sm: 'text-2xl',
      base: '!text-xl',
    },
  });

  return (
    <Flex
      className="flex pt-2 pb-2 justify-between items-center"
      data-testid="topic-level-container"
      id={hash}
    >
      <Flex className="flex ml-3 items-center">
        <H2
          data-testid="topic-level-title"
          className={cn(textBlue, topicLevelTitleResponsiveClasses)}
        >
          {title}
        </H2>
      </Flex>
      <Button
        className={`flex items-center p-3 bg-transparent cursor-pointer ${text5}  hover:bg-transparent active:bg-transparent`}
        data-testid="topic-level-toggle-collapse"
        aria-expanded={isTopicsOpen}
        aria-label={
          isTopicsOpen
            ? formatMessage(
                {
                  id: 'library.library.topic_level.aria_label_collapse',
                  defaultMessage: 'Collapse {topicTitle} sections',
                },
                {
                  topicTitle: title,
                },
              )
            : formatMessage(
                {
                  id: 'library.library.topic_level.aria_label_expand',
                  defaultMessage: 'Expand {topicTitle} sections',
                },
                {
                  topicTitle: title,
                },
              )
        }
        onClick={toggleCollapseAllTopics}
      >
        <P
          className={cn(
            text5,
            '!text-sm font-bold hidden',
            collapseTextResponsiveClasses,
          )}
          data-testid="topic-level-collapsed-title"
        >
          {isTopicsOpen ? (
            <FormattedMessage
              id="library.library.topic_level.topics_state"
              defaultMessage="Collapse all"
            />
          ) : (
            <FormattedMessage
              id="library.library.topic_level.topics_state_expand"
              defaultMessage="Expand all"
            />
          )}
        </P>
        <Box className="ml-1">
          <AnimatePresence initial={false}>
            <motion.div
              initial={{ rotate: isTopicsOpen ? '180deg' : '0deg' }}
              animate={{ rotate: isTopicsOpen ? '0deg' : '180deg' }}
              exit={{ rotate: isTopicsOpen ? '0deg' : '180deg' }}
              transition={{ duration: 0.2 }}
            >
              <Icon icon={faAngleDown} size="1x" />
            </motion.div>
          </AnimatePresence>
        </Box>
      </Button>
    </Flex>
  );
};

export default LibraryTopicLevel;
